import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { AppContent, ContentRow } from "../../../styled/components";
import { getContact, getCountries, getLanguages, getTitles, getUsers } from "../api-service";
import { prepareContactFormData } from "../util/form-functions.util";
import AddressCard from "./address-card";
import ChildContactCard from "./child-contact-card";
import { ContactContainer, ContentRowHeader } from "./contact-form.styles";
import ContactTitle from "./contact-title";

const FieldLabel = ({ label }) => (
  <Typography
    variant="caption"
    color="textSecondary"
    sx={{ display: "block", textTransform: "uppercase", letterSpacing: 0.4 }}
  >
    {label}
  </Typography>
);

const Field = ({ label, value, sx }) => (
  <Box sx={{ flex: 1, minWidth: 200, ...sx }}>
    <FieldLabel label={label} />
    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
      {value && String(value).trim() !== "" ? value : "—"}
    </Typography>
  </Box>
);

const LinkField = ({ label, value, href, sx }) => (
  <Box sx={{ flex: 1, minWidth: 200, ...sx }}>
    <FieldLabel label={label} />
    {value && String(value).trim() !== "" ? (
      <Link
        component={RouterLink}
        to={href}
        underline="hover"
        color="primary"
        cursor="pointer"
      >
        {value}
      </Link>
    ) : (
      <Typography variant="body1">—</Typography>
    )}
  </Box>
);

function ContactShow({ contactId, onEdit, onContactList }) {
  const [contact, setContact] = useState(null);
  const [titles, setTitles] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [contactData, ti, l, u, co] = await Promise.all([
          getContact(contactId),
          getTitles(),
          getLanguages(),
          getUsers(),
          getCountries(),
        ]);
        if (!contactData) {
          throw new Error("Contact not found");
        }
        setContact(contactData);
        setTitles(ti ?? []);
        setLanguages(l ?? []);
        setUsers(u ?? []);
        setCountries(co ?? []);
      } catch (err) {
        setError(err.message || "Error loading contact");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [contactId]);

  if (loading) {
    return (
      <AppContent>
        <ContentRow>
          <Typography variant="h5" component="h1">
            Loading contact data...
          </Typography>
        </ContentRow>
      </AppContent>
    );
  }

  if (error || !contact) {
    return (
      <AppContent>
        <ContentRow>
          <Typography variant="h5" component="h1" color="error">
            {error || "Contact not found"}
          </Typography>
        </ContentRow>
      </AppContent>
    );
  }

  const data = prepareContactFormData(contact);
  const isCompany = data.type === "company";
  const isIndividualWithCompany = !isCompany && !!data.companyId;
  const fullName = `${data.firstName} ${data.lastName}`.trim();
  const displayedName = isCompany ? data.firstName : fullName;

  const typeChipLabel = data.landlord
    ? "Landlord"
    : data.vendor
      ? "Vendor"
      : "Customer";
  const titleName = (titles.find((t) => t.id === data.titleId) || {}).name;
  const languageName = (languages.find((l) => l.code === data.lang) || {}).name;
  const salesPersonName = (users.find((u) => u.id === data.salesPersonId) || {})
    .name;
  const addressesToShow = isIndividualWithCompany
    ? data.parentCompanyAddresses
    : data.addresses;
  const addressesTitle = isIndividualWithCompany
    ? "Company address"
    : "Addresses";

  const parentCompany = contact.parentCompany;

  return (
    <AppContent>
      <ContentRow sx={ContentRowHeader}>
        <Typography variant="h5" component="h1">
          <ContactTitle data={data} contactId={contactId} />
        </Typography>

        <div style={{ display: "flex", gap: "8px" }}>
          <Button variant="outlined" onClick={onContactList}>
            Contact list
          </Button>
          <Button variant="outlined" color="primary" onClick={onEdit}>
            Edit
          </Button>
        </div>
      </ContentRow>

      <Box sx={ContactContainer}>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 180 },
              flexShrink: 0,
              backgroundColor: "#f5f5f5",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRight: { xs: "none", md: "1px solid #ddd" },
            }}
          >
            <Box
              sx={{
                width: 150,
                height: 150,
                borderRadius: "50%",
                backgroundColor: "transparent",
                border: "1px solid",
                borderColor: "action.active",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isCompany ? (
                <BusinessIcon
                  sx={{ fontSize: 96, color: "action.active" }}
                  aria-label="Company contact"
                />
              ) : (
                <PersonIcon
                  sx={{ fontSize: 96, color: "action.active" }}
                  aria-label="Individual contact"
                />
              )}
            </Box>

            <Typography
              variant="h6"
              sx={{ textAlign: "center", width: "100%" }}
            >
              {displayedName}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: "center", mb: 1 }}
            >
              {data.function || ""}
            </Typography>

            <Chip
              label={typeChipLabel}
              variant="outlined"
              color="primary"
              size="small"
            />
          </Box>

          <Box sx={{ flexGrow: 1, padding: { xs: "16px", md: "24px" } }}>
            <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
              Basic information
            </Typography>

            {isCompany ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 2 }}
              >
                <Field label="Company name" value={data.firstName} />
                <Field label="VAT" value={data.vat} />
              </Stack>
            ) : (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 2 }}
              >
                <Field
                  label="Name"
                  value={[titleName, data.firstName, data.lastName]
                    .filter(Boolean)
                    .join(" ")}
                />
              </Stack>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Field label="Email" value={data.email} />
              {!isCompany && (
                <Field label="Job position" value={data.function} />
              )}
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              {!isCompany && (
                <LinkField
                  label="Company"
                  value={parentCompany ? parentCompany.firstName : ""}
                  href={parentCompany ? `/contacts/${parentCompany.id}` : ""}
                />
              )}
              <Field label="Phone number" value={data.phoneNumber} />
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Field label="Language" value={languageName} />
              <Field label="Sales Person" value={salesPersonName} />
            </Stack>

            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  display: "block",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Tags
              </Typography>
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}
              >
                {(data.tags || []).length === 0 ? (
                  <Typography variant="body1">—</Typography>
                ) : (
                  data.tags.map((tag) => (
                    <Chip key={tag.id} label={tag.name} size="small" />
                  ))
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <FieldLabel label="Internal Notes" />
              <Typography
                variant="body1"
                sx={{
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                  mt: 0.5,
                }}
              >
                {data.internalNote &&
                String(data.internalNote).trim() !== ""
                  ? data.internalNote
                  : "—"}
              </Typography>
            </Box>

            <Typography variant="h5" sx={{ mt: 4, mb: 1 }}>
              {addressesTitle}
            </Typography>
            {addressesToShow.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                {isIndividualWithCompany
                  ? "The associated company has no registered address."
                  : "No addresses registered."}
              </Typography>
            ) : (
              addressesToShow.map((address, index) => (
                <AddressCard key={index} address={address} countries={countries} />
              ))
            )}

            {isCompany && data.childContacts.length > 0 && (
              <>
                <Typography variant="h5" sx={{ mt: 4, mb: 1 }}>
                  Company contacts
                </Typography>
                <Grid container spacing={2}>
                  {data.childContacts.map((child) => (
                    <Grid item key={child.id} xs={12} sm={6} md={4}>
                      <ChildContactCard contact={child} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </AppContent>
  );
}

export default ContactShow;
